import { LeftSidebar } from "@/components/app-shell";
import { cn } from "@/lib/utils";
import queries from "@/queries";
import { pushModal } from "@/stores/modal";
// toggle-ui-kit 에 스켈레톤이 없다 — 리포 안 모든 스켈레톤이 쓰는 것으로 맞춘다(ui-kit 규칙 예외)
import { Skeleton } from "@/styles/ui/skeleton";
import { Button } from "@/toggle-ui-kit/button";
import { logHomeServiceAccess } from "@/utils/usage-log";
import { useIsMutating, useQuery } from "@tanstack/react-query";
import { PencilLine } from "lucide-react";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";

import { AiAskButton } from "./ai-ask-button";
import { HOME_CONVERT_MUTATION_KEY } from "./base-report-panel";
import { dDay, formatDeadline, parseIsoDate } from "./deadline-date";
import { DeadlineModal } from "./deadline-modal";
import { StepCard } from "./step-card";
import { STEPS, type Step } from "./steps";

/**
 * 마감일을 받는 동안 h1 과 마감일 줄의 자리를 미리 잡는다. 높이는 받아온 뒤의 모습과 같아야
 * 한다 — h1 은 title-2 한 줄(38px), 마감일 줄은 size 28 버튼(28px), 사이 간격은 부모의 gap-3.
 * 하나라도 어긋나면 응답이 올 때 아래 STEP 목록이 그만큼 밀린다(WXBRL QA: 상단 덜그럭거림).
 * h1 까지 덮는 이유: 받기 전엔 "어떤 업무를..." 였다가 "다음 공시 마감까지 D-6, 어떤 업무를..."
 * 로 문구가 통째로 바뀌어, 자리만 잡아 두면 글자가 튀는 것은 그대로 보인다.
 */
const DeadlineSkeleton = () => (
  <div
    role="status"
    aria-label="공시 마감일을 불러오고 있습니다"
    className="flex flex-col gap-3"
  >
    <Skeleton className="h-[38px] w-full max-w-[560px] rounded-md bg-background-neutral-alternative" />
    <div className="flex items-center gap-4">
      <Skeleton className="h-5 w-[180px] rounded-md bg-background-neutral-alternative" />
      <Skeleton className="h-7 w-[88px] rounded-small bg-background-neutral-alternative" />
    </div>
  </div>
);

const StepPanel = ({ step, hidden }: { step: Step; hidden: boolean }) => (
  <aside
    hidden={hidden}
    aria-label={step.label}
    className={cn(
      "flex w-[500px] shrink-0 flex-col items-end gap-6 overflow-y-auto bg-white px-6 pt-28 pb-20 shadow-large",
      hidden && "hidden",
    )}
  >
    <div className="flex w-full flex-col gap-1">
      <p className="caption-2-semibold font-bold text-blue-500">
        STEP{STEPS.indexOf(step) + 1}
      </p>
      <h2 className="headline-1-semibold truncate font-bold text-label-normal">
        {step.label}
      </h2>
      <p className="label-1-normal-regular text-label-alternative">
        {step.description}
      </p>
    </div>
    {step.Panel && <step.Panel />}
  </aside>
);

export const HomePage = () => {
  const { data: nextDeadline, isPending: deadlinePending } = useQuery(
    queries.nextDisclosureDeadline(),
  );
  const deadline = nextDeadline ? parseIsoDate(nextDeadline.deadline) : null;
  // 열린 STEP 은 URL 에 둔다 — 다른 화면에 다녀와도 뒤로가기가 그 상태의 홈으로 돌려준다
  const [searchParams, setSearchParams] = useSearchParams();
  const opened = STEPS.find((s) => s.id === searchParams.get("step")) ?? null;
  // 닫아도 패널 DOM 은 남아야 작업 상태가 유지된다 — 마지막으로 열었던 STEP 을 들고 있는다.
  // 원천은 URL 하나뿐이고, 여기서 렌더 중에 맞춘다. 로컬에서 따로 올리면 뒤로가기로 URL 만
  // 바뀐 뒤 둘이 엇갈려서, 닫는 순간 안 보던 STEP 패널이 새로 마운트된다
  const [lastOpened, setLastOpened] = useState<Step | null>(opened);
  if (opened && opened !== lastOpened) setLastOpened(opened);
  const selected = opened ?? lastOpened;
  const open = opened !== null;
  // 패널 열고 닫기로 홈 히스토리를 늘리지 않는다. 떠날 때의 URL 만 남아야 뒤로가기가 한 번에 돈다
  // 이 화면이 아는 쿼리는 step 하나뿐이다. 나머지(utm 등)는 건드리지 않는다
  const openStep = (step: Step | null) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (step) next.set("step", step.id);
        else next.delete("step");
        return next;
      },
      { replace: true },
    );
  // 변환 중엔 STEP 이동·패널 닫기를 막는다. 다른 STEP 으로 가면 패널이 key 로 다시 만들어져
  // 결과 처리(파일 비우기)가 끊기고, 닫으면 진행 중인 변환이 보이지 않는다.
  // 막는 건 화면 안의 조작뿐이다. 뒤로/앞으로가기는 이 가드를 안 거친다 — 변환 중에 그걸
  // 누르면 패널이 바뀔 수 있다. 히스토리를 막으려면 블로커가 필요해서 지금은 두고 간다
  const converting =
    useIsMutating({ mutationKey: HOME_CONVERT_MUTATION_KEY }) > 0;
  // STEP 카드와 챗봇 CTA 가 같은 규칙을 쓴다. 막힐 땐 양쪽 다 눌리지 않는 모습으로 보여야 한다
  const canOpenStep = (stepId: Step["id"]) =>
    !converting || selected?.id === stepId;
  // 이미 열린 STEP 을 다시 누르면 화면이 그대로라 새로 들어간 것으로 세지 않는다
  const openStepFrom = (entry: string, step: Step) => {
    if (opened?.id !== step.id) logHomeServiceAccess(entry, step.label);
    openStep(step);
  };

  return (
    <div className="flex h-full w-full">
      <LeftSidebar activeMenu="home" />
      {/* 좁은 창은 짜부러뜨리지 않고 가로 스크롤. 최소 = main 620 + RSB 500 */}
      <div className="flex min-w-0 flex-1 overflow-x-auto">
        <div className="flex h-full min-w-[1120px] flex-1">
          <main
            className="flex min-w-[620px] flex-1 flex-col items-center gap-10 overflow-y-auto px-16 pt-28 pb-[100px]"
            onClick={() => !converting && openStep(null)}
          >
            <div className="flex w-full max-w-[1200px] flex-col gap-3">
              {deadlinePending ? (
                <DeadlineSkeleton />
              ) : (
                <>
                  {/* 마감일을 못 받으면 D-day 와 날짜를 통째로 뺀다. 추측한 날짜는 마감을 놓치게 만든다 */}
                  <h1 className="title-2-bold text-label-normal">
                    {deadline
                      ? `다음 공시 마감까지 ${dDay(deadline, new Date())}, 어떤 업무를 진행해볼까요?`
                      : "어떤 업무를 진행해볼까요?"}
                  </h1>
                  {nextDeadline && deadline && (
                    <div className="flex items-center gap-4">
                      <p className="label-1-normal-medium text-label-alternative">
                        공시 마감일: {formatDeadline(deadline)}
                      </p>
                      <Button
                        size={28}
                        leftIcon={<PencilLine strokeWidth={1.8} />}
                        onClick={(e) => {
                          e.stopPropagation();
                          pushModal(DeadlineModal, { current: nextDeadline });
                        }}
                      >
                        일정 변경
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="flex w-full max-w-[1200px] flex-col gap-4">
              <div className="flex items-center gap-2 rounded-lg border-2 border-background-gray-alternative bg-background-gray-alternative p-3">
                <img
                  className="size-5 shrink-0"
                  src="/icons/info-rounded.svg"
                  alt=""
                />
                <p className="label-1-normal-regular text-label-normal">
                  진행중인 단계를 선택해보세요. 단계에 맞는 기능을
                  안내해드립니다.
                </p>
              </div>
              <div className="flex flex-col gap-2">
                {STEPS.map((step, i) => (
                  <StepCard
                    key={step.id}
                    step={step}
                    order={i + 1}
                    selected={open && selected?.id === step.id}
                    disabled={!canOpenStep(step.id)}
                    onSelect={() => openStepFrom("STEP", step)}
                  />
                ))}
              </div>
            </div>
          </main>
          {/* 닫기는 숨김이라 패널 상태가 남고, 다른 STEP 은 key 가 바뀌어 초기화된다 */}
          {selected && (
            <StepPanel key={selected.id} step={selected} hidden={!open} />
          )}
        </div>
      </div>
      <AiAskButton
        canOpenStep={canOpenStep}
        onOpenStep={(stepId) => {
          const step = STEPS.find((s) => s.id === stepId);
          if (step) openStepFrom("AI 물어보기", step);
          else openStep(null);
        }}
      />
      {/*
        TODO(WXBRL-2580): 이용가이드 플로팅 모달(./guide-floating-modal)은 홈 개편 동안만 미노출.
        되살리는 조건은 "홈 첫 진입 안내를 다시 켜기로 정해지면" — 그때 <GuideFloatingModal /> 를
        여기 넣는 것으로 끝난다(컴포넌트·테스트·store 는 그대로 있다).
        지금은 dismiss 를 부르는 곳이 없어 사이드바 이용가이드 배지(HomeLSB)도 함께 안 뜬다 —
        안내를 영구히 접기로 하면 모달·store·배지를 한 PR 에서 같이 지운다.
      */}
    </div>
  );
};
